// src/theme/enterpriseTheme.ts

/**
 * Material UI enterprise theme configuration for the Quantik Mind frontend.
 *
 * This module defines the visual design system used across the enterprise UI,
 * including:
 * - the global color palette
 * - typography scale and font weights
 * - shared shape and elevation settings
 * - component-level style overrides for the dark enterprise experience
 */

import { createTheme } from '@mui/material/styles';

/**
 * Shared Material UI theme used across the enterprise frontend.
 *
 * The theme is intentionally optimized for:
 * - a dark, high-contrast operator interface
 * - strong brand identity through Quantum green accents
 * - consistent styling for primary layout and interaction components
 */
export const enterpriseTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#39ff14', // Primary brand accent used across key interactive elements.
      light: '#6fff4d',
      dark: '#2dd10f',
      contrastText: '#000000',
    },
    secondary: {
      main: '#00B4FF', // Secondary accent color used for complementary highlights.
      light: '#4DD0FF',
      dark: '#0090CC',
    },
    background: {
      default: '#0A0E14', // Default application background.
      paper: '#13171F', // Elevated surfaces such as cards, panels, and drawers.
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0B8C4',
    },
    divider: 'rgba(57, 255, 20, 0.12)',
    error: {
      main: '#FF3D3D',
    },
    warning: {
      main: '#FF9800',
    },
    success: {
      main: '#39ff14',
    },
  },

  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.75rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '2.25rem',
      fontWeight: 700,
    },
    h4: {
      fontSize: '1.875rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      letterSpacing: 0,
      textTransform: 'none',
    },
  },

  shape: {
    borderRadius: 12,
  },

  /**
   * Custom shadow scale.
   *
   * Material UI expects a fixed-length shadows array. The first entries are
   * customized for the enterprise visual identity, while the remaining entries
   * are intentionally left as neutral placeholders.
   */
  shadows: [
    'none',
    '0 2px 8px rgba(0, 0, 0, 0.4)',
    '0 4px 16px rgba(0, 0, 0, 0.5)',
    '0 8px 24px rgba(0, 0, 0, 0.6)',
    '0 12px 32px rgba(0, 0, 0, 0.7)',
    '0 0 20px rgba(57, 255, 20, 0.3)', // Soft branded glow for highlighted surfaces.
    '0 0 30px rgba(57, 255, 20, 0.4)',
    '0 0 40px rgba(57, 255, 20, 0.5)',
    ...Array(17).fill('none'),
  ] as any,

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#13171F',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#39ff14',
            borderRadius: '4px',
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          minHeight: 44,
          padding: '10px 20px',
          fontSize: '0.95rem',
          lineHeight: 1.2,
          boxShadow: 'none',
          border: '1px solid transparent',
          transition: 'background-color 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
          '&.Mui-focusVisible': {
            outline: '2px solid #39ff14',
            outlineOffset: 2,
          },
          '&.Mui-disabled': {
            opacity: 0.48,
            boxShadow: 'none',
            cursor: 'not-allowed',
            pointerEvents: 'none',
          },
          '&:hover': {
            boxShadow: '0 0 18px rgba(57, 255, 20, 0.28)',
          },
        },
        contained: {
          background: '#39ff14',
          borderColor: '#39ff14',
          color: '#000',
          fontWeight: 700,
          '&:hover': {
            background: '#0A0E14',
            borderColor: '#39ff14',
            color: '#39ff14',
          },
          '&:active': {
            background: '#0A0E14',
            borderColor: '#39ff14',
            color: '#39ff14',
          },
          '&.Mui-disabled': {
            background: 'rgba(255,255,255,0.10)',
            borderColor: 'rgba(255,255,255,0.14)',
            color: 'rgba(255,255,255,0.64)',
          },
        },
        outlined: {
          background: 'transparent',
          borderColor: '#39ff14',
          color: '#FFFFFF',
          borderWidth: '1px',
          '&:hover': {
            borderWidth: '1px',
            borderColor: '#39ff14',
            background: 'rgba(57, 255, 20, 0.08)',
            color: '#39ff14',
          },
          '&:active': {
            color: '#39ff14',
          },
          '&.Mui-disabled': {
            borderColor: 'rgba(255,255,255,0.14)',
            color: 'rgba(255,255,255,0.54)',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(57, 255, 20, 0.1)',
        },
        elevation1: {
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.5)',
        },
        elevation2: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
        },
        elevation3: {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7)',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(57, 255, 20, 0.15)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(57, 255, 20, 0.2)',
            borderColor: 'rgba(57, 255, 20, 0.3)',
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#0A0E14',
          borderBottom: '1px solid rgba(57, 255, 20, 0.2)',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0D1117',
          borderRight: '1px solid rgba(57, 255, 20, 0.15)',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
        filled: {
          backgroundColor: 'rgba(57, 255, 20, 0.15)',
          color: '#39ff14',
          border: '1px solid rgba(57, 255, 20, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(57, 255, 20, 0.25)',
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            minHeight: 44,
            borderRadius: 8,
            '& fieldset': {
              borderColor: 'rgba(57, 255, 20, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(57, 255, 20, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#39ff14',
              borderWidth: '2px',
            },
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          minHeight: 48,
          '&.Mui-selected': {
            color: '#39ff14',
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#39ff14',
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
  },
});

export default enterpriseTheme;
